import React from "react";
import dayjs from "dayjs";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Divider, IconButton, InputAdornment, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimeField } from "@mui/x-date-pickers/TimeField";

const parseTime = (value) => {
  const [hour = 0, minute = 0] = String(value || "00:00").split(":").map(Number);

  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
};

const stepTime = (value, direction) => (
  parseTime(value).add(direction, "minute").format("HH:mm")
);

const toggleMeridiem = (value) => (
  parseTime(value).add(12, "hour").format("HH:mm")
);

const normalizeEndOfDay = (time, allowEndOfDay) => (
  allowEndOfDay && time === "00:00" ? "24:00" : time
);

const ScheduleTimeField = ({
  value,
  disabled = false,
  timeFormat = "ampm",
  allowEndOfDay = false,
  onChange,
  sx,
}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <TimeField
      value={parseTime(value)}
      disabled={disabled}
      format={timeFormat === "24h" ? "HH:mm" : "hh:mm"}
      minutesStep={1}
      sx={[
        sx,
        allowEndOfDay && value === "24:00" && timeFormat === "24h" && {
          "& .MuiOutlinedInput-root": {
            position: "relative",
            "&::after": {
              // Dayjs normalizes 24:00 to 00:00, so mask its rendered value.
              content: '"24:00"',
              position: "absolute",
              top: 0,
              right: "26px",
              left: 0,
              height: "42px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              backgroundColor: "transparent",
              zIndex: 1,
            },
          },
          "& .MuiInputBase-input, & .MuiPickersInputBase-sectionsContainer, & .MuiPickersSectionList-root": {
            color: "transparent !important",
            opacity: 0,
          },
        },
      ]}
      onChange={(newValue) => {
        if (!newValue || !newValue.isValid()) return;
        onChange(normalizeEndOfDay(newValue.format("HH:mm"), allowEndOfDay));
      }}
      slotProps={{
        textField: {
          size: "small",
          inputProps: {
            inputMode: "numeric",
            style: {
              height: "40px",
              padding: "0 0 0 12px",
              lineHeight: "40px",
              width: timeFormat === "24h" ? "84px" : "46px",
              textAlign: "center"
            },
          },
          InputProps: {
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{
                  alignSelf: "stretch",
                  height: "auto",
                  maxHeight: "none",
                  ml: 0,
                  mr: "-6px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {timeFormat === "ampm" && (
                  <Typography
                      onClick={() => !disabled && onChange(normalizeEndOfDay(toggleMeridiem(value), allowEndOfDay))}
                    sx={{
                      minWidth: "18px",
                      mr: "6px",
                      fontSize: "14px",
                      lineHeight: "40px",
                      cursor: disabled ? "default" : "pointer",
                      userSelect: "none",
                    }}
                  >
                    {parseTime(value).format("A")}
                  </Typography>
                )}
                <Divider orientation="vertical" flexItem sx={{ height: "32px", my: "auto", mr: "5px" }} />
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", width: "auto", height: "32px" }}>
                  <IconButton
                    disabled={disabled}
                    onClick={() => onChange(normalizeEndOfDay(stepTime(value, 1), allowEndOfDay))}
                    sx={{ width: "20px", height: "16px", p: 0 }}
                  >
                    <KeyboardArrowUpIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                  <IconButton
                    disabled={disabled}
                    onClick={() => onChange(normalizeEndOfDay(stepTime(value, -1), allowEndOfDay))}
                    sx={{ width: "20px", height: "16px", p: 0 }}
                  >
                    <KeyboardArrowDownIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </Box>
              </InputAdornment>
            ),
          },
        },
      }}
    />
  </LocalizationProvider>
);

export default ScheduleTimeField;
